import React from 'react'
import {
  Box,
  Flex,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
  Heading,
  Button,
  useToast,
  Spinner,
  Input,
} from '@chakra-ui/react'
import dayjs from 'dayjs'
import ReactPaginate from 'react-paginate'
import xhr from 'xhr'
import BasicModal from 'components/modal/Modal'
import useAuth from 'hooks/useAuth'
import { useSWRConfig } from 'swr'
import { AiOutlineCheck } from 'react-icons/ai'
import { Link } from 'react-router-dom'
import { downloadExcel } from 'react-export-table-to-excel'
import { STATUS_LABELS } from 'api/administrador/refunds'

export default function FacturedPaymentsAdmin() {
  const toast = useToast()
  const [payments, setPayments] = React.useState([])
  const [isSaving, setIsSaving] = React.useState('')
  const [student, setStudent] = React.useState([])
  const [parent, setParent] = React.useState(null)
  const [idEst, setIdEst] = React.useState()
  const [isLoading, setIsLoading] = React.useState(true)
  const [downloading, setDownloading] = React.useState(false)
  const [isOpen, setIsOpen] = React.useState(false)
  const [confirmationMessage, setConfirmationMessage] = React.useState('')
  const [fetching, setFetching] = React.useState(false)
  const [currentPage, setCurrentPage] = React.useState(1)
  const [paymentsCount, setPaymentsCount] = React.useState(0)

  const [studentCode, setStudentCode] = React.useState('')
  const [appliedStudentCode, setAppliedStudentCode] = React.useState('')

  const [paymentDate, setPaymentDate] = React.useState('')
  const [appliedPaymentDate, setAppliedPaymentDate] = React.useState('')

  const { user } = useAuth()
  const { mutate } = useSWRConfig()

  const getPayments = async (currentPage) => {
    try {
      setIsLoading(true)

      let studentFilter = '&filters[student][id][$gt]=0'

      if (appliedStudentCode && appliedStudentCode.trim() !== '') {
        studentFilter = `&filters[student][id][$eq]=${appliedStudentCode.trim()}`
      }

      let dateFilter = ''

      if (appliedPaymentDate && appliedPaymentDate.trim() !== '') {
        const start = dayjs(appliedPaymentDate)
          .startOf('day')
          .add(1, 'minute')        // 00:01
          .toISOString()

        const end = dayjs(appliedPaymentDate)
          .endOf('day')            // 23:59:59.999
          .toISOString()

        dateFilter =
          `&filters[payment_date][$gte]=${start}` +
          `&filters[payment_date][$lte]=${end}`
      }

      const response = await xhr(
        `/payments?filters[$or][0][status][$eq]=approved&filters[$or][1][status][$eq]=factured${studentFilter}${dateFilter}&sort[0]=createdAt:DESC&pagination[page]=${currentPage}&pagination[pageSize]=20&populate=deep,3`
      )

      setPaymentsCount(response.data.meta.pagination.pageCount)
      setPayments(response.data.data)
    } catch (error) {
      console.error(error)
    } finally {
      setIsLoading(false)
    }
  }


  React.useEffect(() => {
    getPayments(currentPage)
  }, [currentPage, appliedStudentCode, appliedPaymentDate])

  React.useEffect(() => {
    const getStudent = async () => {
      try {
        setFetching(true)
        const [responseStudent, responseParent] = await Promise.all([
          xhr(`/students/${idEst}?populate=*`),
          xhr(`/parents?filters[students][id][$eq]=${idEst}&populate=*`),
        ])

        setStudent(responseStudent.data.data)
        setParent(responseParent.data.data?.[0] || null)
      } catch (error) {
        console.error(error)
        setFetching(false)
      } finally {
        setFetching(false)
      }
    }

    if (idEst) getStudent()
  }, [idEst])

  const handleFactured = async (idPayment, invoice) => {
    try {
      setIsSaving(idPayment)

      let data

      if (invoice) {
        data = {
          invoice_revised: true,
        }
      } else {
        data = {
          status: 'factured',
        }
      }

      const dataPut = await xhr.put(`/payments/${idPayment}`, { data: data })
      const dataResponse = await xhr(
        `/payments/${dataPut.data.data.id}?populate=deep,3`
      )

      setPayments(
        payments.map((el) => {
          if (el.id === dataResponse.data.data.id) {
            return { ...el, ...dataResponse.data.data }
          }
          return el
        })
      )
      mutate('statsAdmin')
      setConfirmationMessage('')

      toast({
        title: 'El pago se ha facturado correctamente!',
        status: 'success',
        duration: 2000,
        isClosable: true,
      })
    } catch (error) {
      console.error(error)
    } finally {
      setIsSaving('')
    }
  }

  let status = {
    review: 'En Revisión',
    approved: 'Aprobado',
    cancelled: 'Cancelado',
    factured: 'Facturado',
  }

  const handleDownloadExcel = async () => {
    setDownloading(true)

    const response = await xhr(`/payments/getDataForExport`)

    const body = response.data.map((el) => ({
      student: el.student ? el.student.id : '',
      id_payment: el.id_payment,
      amount: el.amount,
      method: el.method,
      date: el.payment_date
        ? dayjs(el.payment_date).format('YYYY-MM-DD')
        : dayjs(el.createdAt).format('YYYY-MM-DD'),
      payment_cel: el.payment_cel,
      typeProgram: el.program_type ? el.program_type.name : '',
      program:
        el.subjects.length > 0
          ? el.subjects[0][el.program_type?.type]
            ? el.subjects[0][el.program_type?.type].name
            : ''
          : '',
      adviser: el.adviser
        ? `${el?.adviser?.user?.first_name} ${el?.adviser?.user?.last_name}`
        : '',
      typeUser: el.state ? el.state : '',
      state: status[el.status],
      checked: el.invoice_revised ? 'SI' : 'NO',
      factured:
        el.status === 'approved' ? 'Pendiente por facturar' : 'Facturado',
      refundStatus: el.refund
        ? STATUS_LABELS[el.refund.status] || el.refund.status
        : 'Sin devolución',
      refundAmount: el.refund ? el.refund.refund_amount : '',
    }))
    let header = [
      'ESTUDIANTE',
      'ID DEL PAGO',
      'MONTO',
      'MÉTODO',
      'FECHA PAGO',
      'CELULAR DEL PAGO',
      'TIPO PROGRAMA',
      'PROGRAMA',
      'ASESOR',
      'TIPO USUARIO',
      'ESTADO',
      'REVISADO',
      'FACTURACION',
      'ESTADO DEVOLUCIÓN',
      'MONTO DEVOLUCIÓN',
    ]

    downloadExcel({
      fileName: `payments-csv`,
      sheet: 'excel',
      tablePayload: {
        header,
        body: body,
      },
    })

    setDownloading(false)
  }

  return (
    <>
      <Flex
        mb="15px"
        justifyContent="space-between"
        alignItems="center"
        flexWrap="wrap"
        gap={3}
      >
        <Button
          colorScheme="green"
          isDisabled={downloading}
          isLoading={downloading}
          onClick={handleDownloadExcel}
        >
          Descargar excel
        </Button>

        <Flex alignItems="center" gap={2}>
          <Input
            size="sm"
            placeholder="Código de estudiante"
            value={studentCode}
            onChange={(e) => setStudentCode(e.target.value)}
            width="200px"
          />

          <Input
            size="sm"
            type="date"
            value={paymentDate}
            onChange={(e) => setPaymentDate(e.target.value)}
            width="200px"
          />

          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setCurrentPage(1)
              setAppliedStudentCode(studentCode)
              setAppliedPaymentDate(paymentDate)
            }}
          >
            Filtrar
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={() => {
              setStudentCode('')
              setAppliedStudentCode('')
              setPaymentDate('')
              setAppliedPaymentDate('')
              setCurrentPage(1)
            }}
          >
            Limpiar
          </Button>
        </Flex>
      </Flex>

      <Box
        backgroundColor="white"
        borderRadius="10px"
        border="1px solid #9e46d8"
        marginRight="10px"
      >
        <Heading
          color="white"
          backgroundColor="#574feb"
          borderTopRadius="8px"
          paddingLeft="10px"
          fontSize="30px"
          fontWeight="bold"
          paddingTop="10px"
          paddingBottom="10px"
        >
          Facturas de pagos
        </Heading>

        {isLoading ? (
          <Flex justifyContent="center" marginY="15px">
            <Spinner size="md" />
          </Flex>
        ) : (
          <>
            <TableContainer px="13px">
              <Table margin="0px" size="xs">
                <Thead>
                  <Tr fontSize="13px">
                    <Th py="10px">Estudiante</Th>
                    <Th>Id</Th>
                    <Th>Monto</Th>
                    <Th>Método</Th>
                    <Th>Fecha Pago</Th>
                    <Th>Programa</Th>
                    <Th>Tipo Usuario</Th>
                    <Th>Facturar</Th>
                    <Th>Revisado</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {payments.map((el) => {
                    const effectiveDate =
                      el.payment_date && dayjs(el.payment_date).isValid()
                        ? dayjs(el.payment_date)
                        : el.createdAt && dayjs(el.createdAt).isValid()
                          ? dayjs(el.createdAt)
                          : null

                    return (
                      <Tr key={el.id}>
                        <Td textAlign="left" py="10px">
                          <Button
                            onClick={() => {
                              setIdEst(el.student.id)
                              setIsOpen(true)
                            }}
                            size="sm"
                          >
                            {el.student?.id}
                          </Button>
                        </Td>
                        <Td fontSize="15px">{el.id_payment}</Td>
                        <Td fontSize="15px">{el.amount}</Td>

                        <Td fontSize="15px">{el.method}</Td>
                        <Td fontSize="15px">
                          {effectiveDate
                            ? effectiveDate.format('YYYY-MM-DD')
                            : ''}
                        </Td>
                        <Td fontSize="15px">
                          <Box whiteSpace="break-spaces" width="160px">
                            <Box mt="5px" fontSize="14px">
                              {el.program_type?.name}
                            </Box>
                            {el.subjects.length > 0 ? (
                              <Box
                                display="flex"
                                alignItems="center"
                                alignContent="center"
                              >
                                <Box fontWeight="500" mr="4px">
                                  Programa:
                                </Box>
                                <Box fontSize="14px" mt="2px">
                                  {
                                    el.subjects[0][
                                      el.program_type?.type
                                    ]?.name
                                  }
                                </Box>
                              </Box>
                            ) : null}
                          </Box>
                        </Td>
                        <Td fontSize="15px">
                          <Box whiteSpace="break-spaces" width="130px">
                            {el?.state}
                          </Box>
                        </Td>
                        <Td>
                          {el.status === 'approved' ? (
                            <Button
                              variant="outline"
                              colorScheme="messenger"
                              isLoading={el.id === isSaving}
                              onClick={() => handleFactured(el.id)}
                              size="xs"
                            >
                              Facturar
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              colorScheme="messenger"
                              isDisabled={true}
                              size="xs"
                            >
                              Facturado
                            </Button>
                          )}
                        </Td>
                        <Td textAlign="center">
                          {!el.invoice_revised ? (
                            confirmationMessage !== el.id ? (
                              <Button
                                variant="outline"
                                colorScheme="messenger"
                                isLoading={el.id === isSaving}
                                onClick={() =>
                                  setConfirmationMessage(el.id)
                                }
                                size="xs"
                              ></Button>
                            ) : (
                              <Box display="flex">
                                <Button
                                  variant="outline"
                                  colorScheme="messenger"
                                  isLoading={el.id === isSaving}
                                  onClick={() =>
                                    handleFactured(el.id, 'invoice')
                                  }
                                  size="xs"
                                >
                                  Confirmar
                                </Button>
                                <Button
                                  variant="outline"
                                  colorScheme="red"
                                  isLoading={el.id === isSaving}
                                  onClick={() =>
                                    setConfirmationMessage('')
                                  }
                                  size="xs"
                                  ml="8px"
                                >
                                  Cancelar
                                </Button>
                              </Box>
                            )
                          ) : (
                            <Button
                              variant="outline"
                              colorScheme="messenger"
                              isDisabled={true}
                              size="xs"
                            >
                              <AiOutlineCheck />
                            </Button>
                          )}
                        </Td>
                      </Tr>
                    )
                  })}
                </Tbody>
              </Table>
            </TableContainer>
            <BasicModal isOpen={isOpen} setIsOpen={setIsOpen}>
              {fetching ? (
                <Spinner />
              ) : (
                <Box>
                  {student.user && (
                    <>
                      <Link
                        to={`/plataforma/administrador/ver-estudiante/${student.id}`}
                        target="_blank"
                      >
                        <Button
                          variant="outline"
                          colorScheme="purple"
                          size="sm"
                        >
                          Ir al perfil
                        </Button>
                      </Link>
                      <br />
                      Estudiante: {student.user?.first_name}{' '}
                      {student.user?.last_name}
                      <br />
                      Documento: {student.identity && student.identity}
                      <br />
                      Tipo Doc:{' '}
                      {student.identity_type &&
                        student.identity_type.name}
                      <br />
                      Dirección: {student.address && student.address}
                      <br />
                      Barrio: {student.neighborhood &&
                        student.neighborhood}
                      <br />
                      Ciudad: {student.city && student.city}
                      <br />
                      Departamento: {student.state && student.state}
                      <br />
                      Correo:{' '}
                      {student.user.email && student.user.email}
                      <br />
                      Celular:{' '}
                      {student.user.cellphone &&
                        student.user.cellphone}
                      <br />
                      Acudiente:{' '}
                      {parent?.full_name && parent.full_name}
                      <br />
                      Doc del acudiente:{' '}
                      {parent?.identity && parent.identity}
                    </>
                  )}
                </Box>
              )}
            </BasicModal>
          </>
        )}
      </Box>
      <Flex justifyContent="flex-end" marginTop="sm">
        <ReactPaginate
          breakLabel="..."
          nextLabel=">"
          onPageChange={(e) => {
            setCurrentPage(e.selected + 1)
          }}
          pageRangeDisplayed={2}
          pageCount={paymentsCount}
          previousLabel="<"
          renderOnZeroPageCount={null}
          containerClassName="containerReactPaginate"
          pageClassName="liReactPaginate"
        />
      </Flex>

      <style jsx>{`
        .containerReactPaginate {
          display: flex;
          width: inherit;
          justify-content: space-evenly;
          list-style: none;
          width: 300px;
        }

        .selected {
          color: #574feb;
          font-weight: bold;
        }
      `}</style>
    </>
  )
}
